// «I MIEI TAVOLI / RIPRENDI» del Burraco: la forma della risposta del BFF di Limulo (`GET /v1/games/burraco/tables/mine`, endpoint
// `qlash.games.burraco.table.mine.list`, capability `burraco.table.mine.list`) e la classificazione dei suoi errori. Funzioni PURE, niente rete:
// la query sta in `services/limuloApi.ts` (`getMyBurracoTables`), il consumatore in `components/burraco/TavoliMiei.tsx`.
//
// La forma e MISURATA, non copiata dal catalogo (Z8 di tma-orcb, 12/10 16:5xZ, collaudo-b dal runner, sola lettura, 200):
//   { ok, organizationId, data: { provider, count, data: [] }, meta: {} }
// cioe DUE livelli di `data`: il primo e la busta del BFF (`unwrapData`), il secondo e quello del game host (`ok = (data, extra) => ({ provider, ...extra,
// data })`, con `extra = { count }`). La lista sta nel SECONDO. Non si scende con `capabilityPayloadOf`: scende solo per gli OGGETTI, e questo e un array.
//
// Ogni voce e `{ tableId, posto, posti, attivo, wsUrl }` (QBurraco `limulo-app/bff-endpoint-catalog.proposal.js:35` e `gamehost-core/src/service/routes.ts:282`
// @ 83ab2d23); `attivo` e «ogni persona ha preso il suo posto almeno una volta» (`room/table.ts:86`), cioe la mano e partita. ⚠️ NIENTE nella voce dice se il
// tavolo e di un torneo (l'`incontro` del tavolo non esce da `/service/tables`): un tavolo di torneo qui e una voce come le altre.
// Il `wsUrl` si IGNORA: l'indirizzo del socket e quello del ticket (`lib/burraco/ticket.ts`), mai questo.

import { burracoTableIdValido } from './ticket';
import { limuloFailure } from '@/lib/limulo/errors';
import { unwrapData } from '@/lib/limulo/model';

/** La rotta del BFF. Una GET: si ritenta come ogni lettura. */
export const BURRACO_TAVOLI_MIEI_ROUTE = '/v1/games/burraco/tables/mine';

/** Un tavolo VIVO di chi chiama. `posti` e il numero dei posti (2 = uno contro uno, 4 = due contro due), o `null` se il game host non lo dice in modo che si legge. */
export interface TavoloMio {
  /** L'uuid del tavolo, MINUSCOLO: e l'`id` della rotta `burraco/tavolo/:id`. */
  tableId: string;
  posti: 2 | 4 | null;
  /** `true` = la mano e partita (tutti hanno preso il posto); `false` = si aspettano ancora i giocatori. */
  attivo: boolean;
}

export type BurracoTavoliMiei = { ok: true; tavoli: TavoloMio[] } | { ok: false };

const asRecord = (v: unknown): Record<string, unknown> => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

/**
 * Lettura CHIUSA sulla forma misurata: `body.data.data` e un array, e nient'altro. Una risposta senza di lui — anche con un altro campo elenco, o con
 * l'array UN livello sopra (`body.data`) — e `ok: false`, mai «nessun tavolo»: il vuoto si afferma solo davanti a un array vuoto vero (e il game host lo
 * dice: «un 200 senza `tables` NON e' non stai giocando», `QBURRACO_UNEXPECTED_RESPONSE`). Una voce che non si legge (senza uuid, o con `attivo` che non e un
 * booleano) rende NULLA l'intera lettura: un pezzo di elenco, detto come l'elenco, sarebbe un vuoto parziale affermato.
 */
export function burracoTavoliMieiOf(body: unknown): BurracoTavoliMiei {
  const lista = asRecord(unwrapData<unknown>(body)).data;
  if (!Array.isArray(lista)) return { ok: false };
  const tavoli: TavoloMio[] = [];
  for (const voce of lista) {
    const r = asRecord(voce);
    const id = typeof r.tableId === 'string' ? r.tableId.trim() : '';
    if (!burracoTableIdValido(id) || typeof r.attivo !== 'boolean') return { ok: false };
    tavoli.push({ tableId: id.toLowerCase(), posti: r.posti === 2 || r.posti === 4 ? r.posti : null, attivo: r.attivo });
  }
  return { ok: true, tavoli };
}

/**
 * Perche una lettura del BFF non ha dato un elenco (Z8: «il codice d'errore sta in `code`; `error` e il messaggio»):
 *  · `non-disponibile` SOLO per `424 E_QLASH_CAPABILITY_DISABLED` (la capability e spenta per l'organizzazione): e uno stato, non un guasto, e riprovare non serve;
 *  · `rete` — non c'e stato HTTP (rete caduta, o scaduta l'attesa): riprovare puo servire;
 *  · `errore` — ogni altro esito (un altro `code`, `E_QLASH_HTTP_<n>`, un 424 `E_QLASH_CAPABILITY_RESOLVER_ERROR`, un 5xx): riprovare puo servire.
 * ⛔ Un 424 qualunque NON e «non disponibile» (lezione di TMA#691 e T1c): il BFF fa uscire col 424 ogni guasto del provider.
 */
export type GuastoTavoliMiei = 'non-disponibile' | 'rete' | 'errore';

export function guastoTavoliMieiOf(errore: unknown): GuastoTavoliMiei {
  const f = limuloFailure(errore);
  if (f.status === 424 && f.code === 'E_QLASH_CAPABILITY_DISABLED') return 'non-disponibile';
  return f.status === null ? 'rete' : 'errore';
}
